import React from 'react';
import Sheet from '../common/Sheet';
import { SPEEDS } from '../hooks/useTimelinePlayhead';
import { FindEventsChips } from './WhenFields';

/**
 * MoreSheet — the mobile-portrait "⋯ More" bottom sheet (#86bb7phhh). On a phone the timeline card head
 * has room only for the transport + span + this ⋯ trigger, so the SECONDARY controls consolidate here:
 *
 *   • Speed    — the SAME SPEEDS presets the transport shows inline on tablet/desktop
 *   • Filters  — the SAME FindEventsChips the ribbon shows inline
 *   • Bulk     — the SAME bulk-action items (deriveBulkItems) the banks-row ActionsMenu shows
 *   • Options  — the SAME cog toggles (Auto-skip / Camera Info / Always-show quick actions)
 *
 * It composes those existing controls — no new control logic. The ⋯ trigger is CSS-hidden ≥768px, where
 * every one of these is reachable inline (Filters on the card, Speed in the transport, Bulk + Options on
 * the banks row), so this sheet is a mobile-portrait affordance only. Speed/Filters/Bulk/Options match the
 * mock's ⋯ order.
 */
const SPEED_LABELS = { 0.25: '¼x', 0.5: '½x', 1: '1x', 2: '2x', 4: '4x' };

const ToggleRow = ({ label, title, checked, onChange }) => (
  <label className="mcpb-cog-popover__row" title={title}>
    <span className="mcpb-cog-popover__row-label">{label}</span>
    <span className="mcpb-switch">
      <input className="mcpb-switch__input" type="checkbox" aria-label={label} checked={checked} onChange={onChange} />
      <span className="mcpb-switch__track"><span className="mcpb-switch__thumb" /></span>
    </span>
  </label>
);

const MoreSheet = ({
  speed, onSpeed, filters = {}, onSetFilters, bulkItems = [],
  autoSkip, onToggleAutoSkip, videoInfo, onToggleVideoInfo,
  quickActionsAlways, onToggleQuickActions,
  onClose,
}) => (
  <Sheet className="mcpb-sheet mcpb-sheet--more" scrimClassName="mcpb-sheet-scrim" title="More" ariaLabel="More playback controls" onClose={onClose}>
    <div className="mcpb-more">
      <section className="mcpb-more__section">
        <h4 className="mcpb-more__title">Speed</h4>
        <div className="mcpb-sheet__seg" role="group" aria-label="Playback speed">
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              className={`mcpb-sheet__seg-btn${s === speed ? ' mcpb-sheet__seg-btn--active' : ''}`}
              aria-pressed={s === speed}
              onClick={() => onSpeed(s)}
            >
              {SPEED_LABELS[s]}
            </button>
          ))}
        </div>
      </section>

      <section className="mcpb-more__section">
        <h4 className="mcpb-more__title">Filters</h4>
        <p className="mcpb-sheet__hint">Show only clips tagged with the selected types (filters the loaded window &mdash; no re-search).</p>
        <FindEventsChips filters={filters} onToggle={(k) => onSetFilters({ ...filters, [k]: !filters[k] })} />
      </section>

      {bulkItems.length > 0 && (
        <section className="mcpb-more__section">
          <h4 className="mcpb-more__title">Bulk actions</h4>
          <div className="mcpb-more__bulk">
            {bulkItems.map((it) => (
              <button
                key={it.action}
                type="button"
                className="mcpb-more__bulk-btn"
                title={it.title}
                disabled={it.disabled}
                onClick={() => { if (it.onClick) it.onClick(); onClose(); }}
              >
                {it.path && <svg viewBox="0 0 24 24" aria-hidden="true"><path d={it.path} /></svg>}
                <span>{it.label}</span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="mcpb-more__section">
        <h4 className="mcpb-more__title">Options</h4>
        {onToggleAutoSkip && (
          <ToggleRow label="Auto skip gaps" title="Skip over gaps with no recorded video" checked={!!autoSkip} onChange={onToggleAutoSkip} />
        )}
        {onToggleVideoInfo && (
          <ToggleRow label="Camera Info overlay" title="Show resolution, FPS, and stream profile on each camera" checked={!!videoInfo} onChange={onToggleVideoInfo} />
        )}
        {onToggleQuickActions && (
          <ToggleRow label="Always-show quick actions" title="Always show the Snapshot / Download / Archive / Export buttons on every camera (off = reveal on hover or tap)" checked={!!quickActionsAlways} onChange={onToggleQuickActions} />
        )}
      </section>

      <button className="mcpb-sheet__done" type="button" onClick={onClose}>Done</button>
    </div>
  </Sheet>
);

export default MoreSheet;
